import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import VAllergenStatusIcon from '@/components/allergens/VAllergenStatusIcon.vue';
import { useTranslations } from "@/hooks/useTranslations";
import { getBoldString } from '@/utils/Helpers';
import { getAllergenStatusMeta } from '@/utils/allergenStatus';
import type { Filter } from '@/models/ListRequest';
import type { WellnessItem } from '@/models/wellness/WellnessItem';
import type { WellnessTab } from '@/models/wellness/WellnessTab';
import {
  listWellnessItems,
  searchCombinedRecipes,
  searchCustomIngredients,
  searchRecipes,
} from '../../services/wellnessApi';
import vSelectFx from '../vSelectFx.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'WellnessSidebar',
  props: {
    refreshKey: null,
    selectedItemId: null,
    selectedTab: null,
    includeCustomRecipes: { type: Boolean },
    showAllergenStatus: { type: Boolean },
    customRecipesTabLabel: null
  },
  emits: ['select', 'tab-change', 'open-stats', 'open-usage'],
  setup(__props: any, { emit }) {

const props = __props as {
  refreshKey?: number
  selectedItemId?: number | null
  selectedTab?: WellnessTab
  includeCustomRecipes?: boolean
  showAllergenStatus?: boolean
  customRecipesTabLabel?: string
};





const vm = getCurrentInstance();
const translations = useTranslations();
const searchElementId = 'searchWellnessItems';
const activeTab = ref<WellnessTab>(props.selectedTab || 'recipes');
const page = ref(1);
const pageSize = ref(10);
const orderType = ref<'asc' | 'desc'>('asc');
const loading = ref(true);
const hasLoadedOnce = ref(false);
const locatingSelectedItemPage = ref(false);
const shouldLocateSelectedItemPage = ref(false);
const count = ref(0);
const totalCount = ref(0);
const items = ref<WellnessItem[]>([]);
const searchFilters = ref<Filter[]>([]);

const tabs = computed<Array<{ key: WellnessTab; label: string }>>(() => {
  const visibleTabs: Array<{ key: WellnessTab; label: string }> = [
    { key: 'recipes', label: translations.value.get('wellness_tab_recipe') },
    { key: 'combinedRecipes', label: translations.value.get('wellness_tab_combined_recipe') },
  ];

  if (props.includeCustomRecipes) {
    visibleTabs.push({
      key: 'customRecipes',
      label: props.customRecipesTabLabel || translations.value.get('wellness_tab_custom_ingredients'),
    });
  }

  return visibleTabs;
});

const isPrevDisabled = computed(() => page.value === 1);
const isNextDisabled = computed(() => Math.ceil(totalCount.value / pageSize.value) <= page.value);
const showControls = computed(() => totalCount.value > count.value);
const tableColumnCount = computed(() => props.showAllergenStatus ? 4 : 3);

function showError(error: any) {
  const message = error?.response?.data?.errorMessages?.[0]?.userVisibleMessage || 'Network error';
  vm?.proxy?.$snotify?.error(message);
}

async function requestItems() {
  loading.value = !hasLoadedOnce.value;

  try {
    const response = await listWellnessItems(
      activeTab.value,
      page.value,
      pageSize.value,
      orderType.value,
      searchFilters.value
    );

    items.value = response.result;
    count.value = response.count;
    totalCount.value = response.totalCount;
    hasLoadedOnce.value = true;

    if (shouldLocateSelectedItemPage.value) {
      await ensureSelectedItemPage();
    }

    selectRequestedItem();
  }
  catch (error) {
    if (!hasLoadedOnce.value) {
      items.value = [];
      count.value = 0;
      totalCount.value = 0;
    }
    showError(error);
  }
  finally {
    loading.value = false;
  }
}

function setTab(tab: WellnessTab) {
  if (activeTab.value === tab) {
    return;
  }

  activeTab.value = tab;
}

function orderPageSize(value: string) {
  if (value === '0') {
    pageSize.value = 10;
  }
  else if (value === '1') {
    pageSize.value = 20;
  }
  else if (value === '2') {
    pageSize.value = 50;
  }
  else if (value === '3') {
    pageSize.value = 100;
  }
  else {
    pageSize.value = Number(value) || 10;
  }
}

function order(value: string) {
  orderType.value = value === '1' ? 'desc' : 'asc';
}

function next() {
  if (!isNextDisabled.value) {
    page.value += 1;
  }
}

function prev() {
  if (!isPrevDisabled.value) {
    page.value -= 1;
  }
}

function selectItem(item: WellnessItem) {
  emit('select', {
    item,
    tab: activeTab.value,
  });
}

function isSelectedItem(item: WellnessItem) {
  return props.selectedTab === activeTab.value && Number(item.id) === props.selectedItemId;
}

function openStats(item: WellnessItem) {
  emit('open-stats', {
    item,
    tab: activeTab.value,
  });
}

function openUsage(item: WellnessItem) {
  emit('open-usage', {
    item,
    tab: activeTab.value,
  });
}

function getAllergenStatusVariant(item: WellnessItem) {
  return getAllergenStatusMeta(item).variant;
}

function getAllergenStatusTitle(item: WellnessItem) {
  return getAllergenStatusMeta(item).title;
}

function selectRequestedItem() {
  if (!props.selectedItemId || props.selectedTab !== activeTab.value) {
    return;
  }

  const requestedItem = items.value.find((item) => Number(item.id) === props.selectedItemId);
  if (requestedItem && !isSelectedItem(requestedItem)) {
    selectItem(requestedItem);
  }
}

async function ensureSelectedItemPage() {
  if (
    locatingSelectedItemPage.value
    || !shouldLocateSelectedItemPage.value
    || !props.selectedItemId
    || !props.selectedTab
    || props.selectedTab !== activeTab.value
  ) {
    return;
  }

  const selectedId = Number(props.selectedItemId);
  if (items.value.some((item) => Number(item.id) === selectedId)) {
    shouldLocateSelectedItemPage.value = false;
    return;
  }

  const totalPages = Math.ceil(totalCount.value / pageSize.value);
  if (totalPages <= 1) {
    return;
  }

  locatingSelectedItemPage.value = true;

  try {
    for (let nextPage = 1; nextPage <= totalPages; nextPage += 1) {
      if (nextPage === page.value) {
        continue;
      }

      const response = await listWellnessItems(
        activeTab.value,
        nextPage,
        pageSize.value,
        orderType.value,
        searchFilters.value
      );

      const hasItem = response.result.some((item) => Number(item.id) === selectedId);
      if (hasItem) {
        shouldLocateSelectedItemPage.value = false;
        page.value = nextPage;
        return;
      }
    }
  }
  finally {
    locatingSelectedItemPage.value = false;
  }
}

function getSearchPlaceholder() {
  if (activeTab.value === 'recipes') {
    return translations.value.get('wellness_search_recipe');
  }

  if (activeTab.value === 'combinedRecipes') {
    return translations.value.get('wellness_search_combined_recipe');
  }

  return translations.value.get('wellness_search_custom_ingredients');
}

function processRecipeSearchResults(searchTerm: string, response: any) {
  const data: any[] = [];
  data.push({ id: 'cat-0', text: getBoldString(translations.value.get('wellness_all_recipes'), '') });

  response.data.result.map((category: any) => {
    const children: any[] = [];

    if (category.recipes && category.recipes.length) {
      data.push({ id: `cat-${category.id}`, text: getBoldString(category.name, '') });

      category.recipes.map((recipe: any) => {
        children.push({ id: recipe.id, text: getBoldString(recipe.name, searchTerm) });
      });
    }

    data.push({ children });
  });

  return data;
}

function processCombinedSearchResults(searchTerm: string, response: any) {
  const data: any[] = [];
  data.push({ id: 0, text: translations.value.get('wellness_all_combined_recipes') });

  response.data.result.map((item: any) => {
    data.push({ id: item.id, text: getBoldString(item.name, searchTerm) });
  });

  return data;
}

function processIngredientSearchResults(searchTerm: string, response: any) {
  const data: any[] = [];
  data.push({ id: 0, text: getBoldString(translations.value.get('wellness_all_custom_ingredients'), '') });

  response.data.result.ingredientList.result.map((item: any) => {
    data.push({ id: item.id, text: getBoldString(item.name, searchTerm) });
  });

  return data;
}

async function initSearch() {
  await nextTick();

  const selector = (<any>$)(`#${searchElementId}`);
  if (!selector.length) {
    return;
  }

  if (selector.hasClass('select2-hidden-accessible')) {
    selector.off();
    selector.select2('destroy');
  }

  let searchTerm = '';

  const restoreSearchTerm = () => {
    nextTick(() => {
      const select2 = selector.data('select2');
      if (!select2 || !select2.dropdown || !select2.dropdown.$search) {
        return;
      }

      const $search = select2.dropdown.$search;
      $search
        .val(searchTerm)
        .trigger('input')
        .off('input.wellnessreset')
        .on('input.wellnessreset', async() => {
          searchTerm = $search.val();

          if (!searchTerm) {
            searchFilters.value = [];
            page.value = 1;
            await requestItems();
          }
        });
    });
  };

  selector.select2({
    allowClear: false,
    width: '100%',
    placeholder: getSearchPlaceholder(),
    minimumInputLength: 3,
    ajax: {
      delay: 500,
      transport: async(params: any, success: any, failure: any) => {
        try {
          searchTerm = params.data.term || '';

          if (activeTab.value === 'recipes') {
            success(await searchRecipes(searchTerm));
            return;
          }

          if (activeTab.value === 'combinedRecipes') {
            success(await searchCombinedRecipes(searchTerm));
            return;
          }

          success(await searchCustomIngredients(pageSize.value, searchTerm, orderType.value));
        }
        catch (error) {
          failure(error);
          showError(error);
        }
      },
      processResults: (response: any) => {
        if (activeTab.value === 'recipes') {
          return { results: processRecipeSearchResults(searchTerm, response) };
        }

        if (activeTab.value === 'combinedRecipes') {
          return { results: processCombinedSearchResults(searchTerm, response) };
        }

        return { results: processIngredientSearchResults(searchTerm, response) };
      },
    },
    escapeMarkup(element: string) {
      return element;
    },
  })
    .on('select2:select', async(eventData: any) => {
      const value = String(eventData.target.value || '');

      page.value = 1;

      if (activeTab.value === 'recipes' && value.startsWith('cat-')) {
        const categoryId = value.split('-')[1];
        searchFilters.value = categoryId === '0'
          ? []
          : [{ name: 'RecipeCategoryId', value: categoryId }];
      }
      else if (value === '0') {
        searchFilters.value = [];
      }
      else {
        searchFilters.value = [{ name: 'Id', value }];
      }

      await requestItems();

      const selected = items.value.find((item) => `${item.id}` === value);
      if (selected) {
        selectItem(selected);
      }
    })
    .on('select2:open', () => {
      restoreSearchTerm();
    });
}

watch([pageSize, orderType], async() => {
  page.value = 1;
  await requestItems();
});

watch(page, async() => {
  await requestItems();
});

watch(activeTab, async() => {
  searchFilters.value = [];
  page.value = 1;
  emit('tab-change', activeTab.value);
  await requestItems();
  await initSearch();
});

watch(() => props.refreshKey, async() => {
  await requestItems();
  await initSearch();
});

watch([() => props.selectedItemId, () => props.selectedTab], async() => {
  if (props.selectedTab && activeTab.value !== props.selectedTab) {
    shouldLocateSelectedItemPage.value = true;
    activeTab.value = props.selectedTab;
    return;
  }

  shouldLocateSelectedItemPage.value = true;
  await ensureSelectedItemPage();
  selectRequestedItem();
});

onMounted(async() => {
  await requestItems();
  await initSearch();
});

onBeforeUnmount(() => {
  const selector = (<any>$)(`#${searchElementId}`);
  if (selector.hasClass('select2-hidden-accessible')) {
    selector.off();
    selector.select2('destroy');
  }
});

return { __sfc: true,props, emit, vm, translations, searchElementId, activeTab, page, pageSize, orderType, loading, hasLoadedOnce, locatingSelectedItemPage, shouldLocateSelectedItemPage, count, totalCount, items, searchFilters, tabs, isPrevDisabled, isNextDisabled, showControls, tableColumnCount, showError, requestItems, setTab, orderPageSize, order, next, prev, selectItem, isSelectedItem, openStats, openUsage, getAllergenStatusVariant, getAllergenStatusTitle, selectRequestedItem, ensureSelectedItemPage, getSearchPlaceholder, processRecipeSearchResults, processCombinedSearchResults, processIngredientSearchResults, initSearch, VAllergenStatusIcon, vSelectFx }
}

})